import { useState } from 'react';
import { Loader2, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useT } from '@/contexts/I18nContext';
import type { RouterCheckKind, RouterCheckResponse } from '@/api/zoneRouter';
import { Tooltip as UiTooltip } from '@/components/ui/tooltip';

/**
 * Per-host connectivity-check button row.
 *
 * Four visible states per button (finding #18 — the original render was
 * a bare red ✕ with no actionable copy):
 *
 *   never-run → muted "—" + tooltip "нажмите для проверки"
 *   in-flight → spinner + "проверяем…"
 *   success   → green dot + "{latency_ms}ms"
 *   fail      → red icon + operator-readable typed-error copy + the
 *               raw detail in `title=` for power-user inspection
 *
 * Pure render — caller supplies the async `onCheck` to keep the
 * component reusable across router and LC sources.
 *
 * Re-used by HostDesignationMenu (inside the three-dots popover) and
 * potentially Group D's LocalComputer peer list — that's why the runner
 * is injected rather than baked in.
 */
export interface ConnectivityCheckButtonsProps {
  /** Which kinds to expose. RTSP only makes sense for hosts believed
   *  to expose 554; the menu hides it for non-camera designations. */
  kinds: readonly RouterCheckKind[];
  /** Tiny rendering tweak — inline rows fit the popover; column reads
   *  better when standalone (e.g. inside a designate-host dialog). */
  layout?: 'inline' | 'column';
  /** Async runner. Caller bakes in zone id / lan_host / port / path
   *  via closure. Receives only the kind. */
  onCheck: (kind: RouterCheckKind) => Promise<RouterCheckResponse>;
}

type State =
  | { kind: 'idle' }
  | { kind: 'running' }
  | { kind: 'ok'; latency_ms: number | null | undefined }
  | { kind: 'fail'; code: string; message: string; detail: string };

/**
 * Map a BE typed-error code into an operator-readable Russian/English
 * copy. Codes the BE-A verifier emits today:
 *   - host_unreachable         pingable host went silent
 *   - port_closed              TCP RST or timeout on the requested port
 *   - rtsp_unauthorized        camera answered RTSP DESCRIBE with 401
 *   - rtsp_no_stream           camera answered DESCRIBE with 404/no media
 *   - timeout                  any kind of read timeout
 *   - endpoint_not_deployed    FE-synthetic when /check/ returns 404
 *   - target_required          FE-synthetic during BE-B rollout fallback
 * Anything not in this list falls through to the BE `detail` (or the
 * generic `nothing answered` copy).
 */
function copyForError(t: (k: string) => string, code: string): string {
  switch (code) {
    case 'host_unreachable':
      return t('equipment.discoveredPeer.check.err.host_unreachable');
    case 'port_closed':
      return t('equipment.discoveredPeer.check.err.port_closed');
    case 'rtsp_unauthorized':
      return t('equipment.discoveredPeer.check.err.rtsp_unauthorized');
    case 'rtsp_no_stream':
      return t('equipment.discoveredPeer.check.err.rtsp_no_stream');
    case 'timeout':
      return t('equipment.discoveredPeer.check.err.timeout');
    case 'endpoint_not_deployed':
      return t('equipment.discoveredPeer.import.error.endpoint_not_deployed');
    case 'target_required':
      return t('equipment.discoveredPeer.check.err.target_required');
    default:
      return '';
  }
}

export function ConnectivityCheckButtons({
  kinds,
  layout = 'inline',
  onCheck,
}: ConnectivityCheckButtonsProps) {
  const { t } = useT();
  const [states, setStates] = useState<Record<string, State>>({});

  const run = async (k: RouterCheckKind) => {
    setStates(s => ({ ...s, [k]: { kind: 'running' } }));
    try {
      const r = await onCheck(k);
      if (r.ok) {
        setStates(s => ({ ...s, [k]: { kind: 'ok', latency_ms: r.latency_ms } }));
      } else {
        const detail = r.detail || '';
        const friendly = copyForError(t, r.error);
        setStates(s => ({
          ...s,
          [k]: {
            kind: 'fail',
            code: r.error,
            message: friendly || detail || t('equipment.discoveredPeer.check.fail'),
            detail,
          },
        }));
      }
    } catch {
      setStates(s => ({
        ...s,
        [k]: {
          kind: 'fail',
          code: 'transport_error',
          message: t('equipment.discoveredPeer.check.fail'),
          detail: '',
        },
      }));
    }
  };

  const labelOf = (k: RouterCheckKind) =>
    k === 'ping'
      ? t('equipment.discoveredPeer.check.ping')
      : k === 'tcp_open'
        ? t('equipment.discoveredPeer.check.tcp')
        : t('equipment.discoveredPeer.check.rtsp');

  return (
    <div
      className={cn(
        'gap-1',
        layout === 'inline' ? 'flex flex-wrap items-center' : 'grid grid-cols-1',
      )}
      role="group"
    >
      {kinds.map(k => {
        const state = states[k] || { kind: 'idle' };
        // Tooltip — idle nudges the operator; fail surfaces both the
        // code AND the BE-supplied detail so power users can debug
        // without needing devtools.
        const tooltip =
          state.kind === 'idle'
            ? t('equipment.discoveredPeer.check.idleTooltip')
            : state.kind === 'running'
              ? t('equipment.discoveredPeer.check.running')
              : state.kind === 'ok'
                ? labelOf(k)
                : state.kind === 'fail'
                  ? [labelOf(k), state.code, state.detail].filter(Boolean).join(' — ')
                  : labelOf(k);
        return (
          <UiTooltip key={k} content={tooltip}><button
            type="button"
            onClick={() => { void run(k); }}
            disabled={state.kind === 'running'}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-md border border-border bg-card/60 px-2 py-1 text-[11px] font-medium text-foreground/90 transition-colors',
              'hover:bg-muted hover:text-foreground',
              'disabled:cursor-wait disabled:opacity-70',
              state.kind === 'ok' && 'border-[color:var(--color-success)]/30 bg-[color:var(--color-success)]/8 text-[color:var(--color-success)]',
              state.kind === 'fail' && 'border-destructive/30 bg-destructive/8 text-destructive',
            )}
            data-testid={`peer-check-${k}`}
            data-state={state.kind}
          >
            {state.kind === 'idle' && (
              <span className="font-mono text-[10px] tabular-nums text-muted-foreground/70" aria-hidden>
                —
              </span>
            )}
            {state.kind === 'running' && (
              <Loader2 className="size-3 animate-spin" aria-hidden />
            )}
            {state.kind === 'ok' && (
              <span
                className="inline-block size-1.5 rounded-full bg-[color:var(--color-success)]"
                aria-hidden
              />
            )}
            {state.kind === 'fail' && <X className="size-3" aria-hidden />}
            <span className="whitespace-nowrap">{labelOf(k)}</span>
            {state.kind === 'running' && (
              <span className="text-[10px] italic opacity-80">
                {t('equipment.discoveredPeer.check.running')}
              </span>
            )}
            {state.kind === 'ok' && typeof state.latency_ms === 'number' && (
              <span className="tabular-nums text-[10px] opacity-80">
                {t('equipment.discoveredPeer.check.ms').replace('{{ms}}', String(state.latency_ms))}
              </span>
            )}
            {state.kind === 'fail' && (
              <span className="truncate text-[10px] opacity-80" data-testid={`peer-check-${k}-msg`}>
                {state.message}
              </span>
            )}
          </button></UiTooltip>
        );
      })}
    </div>
  );
}

export default ConnectivityCheckButtons;
